import styled, { css } from "styled-components"
import { iconName } from "../icons/Icons";
import { ColorSystem } from "../colors/color";
import { assertNever } from "../../amorg-common";

export const ButtonVariants = ["filled", "outlined", "text", "destructive", "destructive-text", "light", "clean", "dimmed"] as const;
export type ButtonVariant = typeof ButtonVariants[number];
export type ButtonSize = "default" | "magnum";

type ButtonStyleConfig = {
    border:string,
    text:string,
    textHover:string,
    textDisabled:string,
    background:string,
    backgroundHover:string,
    backgroundDisabled:string,
}

function getButtonStyleConfig(variant:ButtonVariant, colorSystem:ColorSystem):ButtonStyleConfig
{
    switch(variant)
    {
        case "filled": return colorSystem.button.filled;
        case "outlined": return colorSystem.button.outlined;
        case "text": return colorSystem.button.text;
        case "destructive": return colorSystem.button.destructive;
        case "destructive-text": return colorSystem.button.destructiveText;
        case "light": return colorSystem.button.light;
        case "clean": return colorSystem.button.clean;
        case "dimmed": return colorSystem.button.dimmed;

        default: assertNever(variant);
    }
}

export const buttonStyling = (colorSystem:ColorSystem) => ({

    getConfig: getButtonStyleConfig,

    handleButtonBackground: (variant: ButtonVariant) => 
        getButtonStyleConfig(variant, colorSystem).background
    ,

     handleButtonHoverBackground: (variant: ButtonVariant) =>
        getButtonStyleConfig(variant, colorSystem).backgroundHover
    ,

    handleButtonColor: (variant: ButtonVariant) => 
        getButtonStyleConfig(variant, colorSystem).text
    ,

    handleButtonHoverColor: (variant: ButtonVariant) => 
        getButtonStyleConfig(variant, colorSystem).textHover
    ,

    handleButtonBackgroundDisabled: (variant: ButtonVariant) => 
        getButtonStyleConfig(variant, colorSystem).backgroundDisabled
    ,

    handleButtonColorDisabled: (variant: ButtonVariant) =>
        getButtonStyleConfig(variant, colorSystem).textDisabled,

    handleButtonBorder: (variant: ButtonVariant) =>
        getButtonStyleConfig(variant, colorSystem).border,
        
    handleButtonHoverOpacity: (variant: ButtonVariant) => {
        switch (variant) {
            case "filled":
                return `0.32`;
            case "outlined":
                return `1`;
            case "text":
                return `1`;
            case "destructive":
                return `0.32`;
            case "destructive-text":
                return `1`;
            case "light":
                return `1`;
            case "destructive-text":
                return `0.32`;
            case "clean":
                return `1`;
            default:
                return `0.32`;
        }
    },

    handleButtonHoverPressedOpacity: (variant: ButtonVariant) => {
        switch (variant) {
            case "filled":
                return `0.64`;
            case "outlined":
                return `0.24`;
            case "text":
                return `0.24`;
            case "destructive":
                return `0.64`;
            case "destructive-text":
                return `1`;
            case "light":
                return `0.24`;
            case "destructive-text":
                return `0.64`;
            case "clean":
                return `1`;
            default:
                return `0.64`;
        }
    }
});


export const ButtonHover = styled.div<{ variant: ButtonVariant, disabled?:boolean }>`
    background: ${props => buttonStyling(props.theme.colors).handleButtonHoverBackground(props.variant)};
    position: absolute;
    top:0;
    right:0;
    bottom:0;
    left:0;  
    opacity:0;      
    transition: all 0.5s ease;
`

export const IconWrapper = styled.div<{ variant: ButtonVariant, hasText?:boolean  }>`
    position: relative;
    height: 20px;
    z-index: 101;
    

    & svg{
        fill: ${props => buttonStyling(props.theme.colors).handleButtonColor(props.variant)};
        height: 20px;
        width: 20px;
    }

    &:hover{
        cursor:pointer;
    }
`;

export const ButtonStyle = css<{ variant: ButtonVariant, icon?: iconName, disabled?:boolean, magnum?: boolean, heightOverride?:string, size?:ButtonSize, iconPosition?: string}>`
    align-items: center;
    background: ${props => buttonStyling(props.theme.colors).handleButtonBackground(props.variant)};
    color: ${props => buttonStyling(props.theme.colors).handleButtonColor(props.variant)};
    border: ${props => buttonStyling(props.theme.colors).handleButtonBorder(props.variant)};
    border-radius:4px;
    display:inline-flex;
    font-size: 16px;
    font-family: "Noto Sans", sans-serif;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 0.25px;
    justify-content: center;
    text-decoration: none;
    height: ${ props => props.size === "magnum" ? "64px;" : "32px;"};
    ${ props => props.heightOverride && `height: ${props.heightOverride};` }
    padding: 1px 8px;
    position: relative;

    flex-direction: ${props => props.iconPosition === "right" ? "row-reverse" : "row"};
    white-space: nowrap;

    & span{

        padding: 0 8px;
        position: relative;
        z-index: 100;

        &:hover{
            cursor: pointer;    
        }
    }

    &:hover, &:focus-visible{
        color: ${props => buttonStyling(props.theme.colors).handleButtonHoverColor(props.variant)};
        & svg{
            fill: ${props => buttonStyling(props.theme.colors).handleButtonHoverColor(props.variant)};
        }
    }

    &:active{
        
    }

    &:hover ${ButtonHover}, &:focus-visible ${ButtonHover}{
        border-radius: inherit;
        cursor: pointer;
        opacity: ${props => buttonStyling(props.theme.colors).handleButtonHoverOpacity(props.variant)};
    }

    &:focus-visible{
        outline:0;
    }


    &:active ${ButtonHover}{
        border-radius: inherit;
        cursor: pointer;
        opacity: ${props => buttonStyling(props.theme.colors).handleButtonHoverPressedOpacity(props.variant)};
    }


    ${props => {
        if(props.disabled){
            return css`
                
                &:disabled{
                    background: ${buttonStyling(props.theme.colors).handleButtonBackgroundDisabled(props.variant)};
                    color:  ${buttonStyling(props.theme.colors).handleButtonColorDisabled(props.variant)};
                
                    & div{
                        background: ${buttonStyling(props.theme.colors).handleButtonBackgroundDisabled(props.variant)};
                        cursor: not-allowed
                    }

                    & svg{
                        fill: ${buttonStyling(props.theme.colors).handleButtonColorDisabled(props.variant)};
                    }

                    &:hover div{
                        cursor: not-allowed
                    }    

                    &:hover span {
                        cursor: not-allowed;
                    }

                    &:hover ${IconWrapper} {
                        cursor: not-allowed;
                    }
                }
          
            `;
        }
    }}
`